/// <reference path="_references.ts"/>

module App.TimeEvent {
	class TimeEventController {
		protected loading: boolean = true;
		protected saving: boolean = false;
		protected userId: number = null;
		private userLoaded: boolean = false;
		private loadEventsPending: boolean = false;
		public event: TimeEvent = null;
		public events: TimeEvent[] = [];
		public loadingEvents: boolean = false;
		public formattedTotal: string;

		constructor(
			protected TimeEventService: TimeEventService,
			protected UserSession: App.User.UserSession,
			public ToastService: App.Utils.ToastService
		) {
			this.UserSession.getUser()
				.then(u => this.userId = u.id)
				.then(u => this.loadEventsPending && this.loadEvents())
				.finally(() => this.userLoaded = true);
		}

		protected getMoment(date: Date, time: Date): moment.Moment {
			return moment(moment(date).format('YYYY-MM-DD') + ', ' + moment(time).format('HH:mm'), 'YYYY-MM-DD, HH:mm');
		}

		protected formatInterval(start: moment.Moment, end: moment.Moment): string {
			return (end.clone().add(1, 'minute') < start ? '-' : '') + this.formatTime(Math.abs(Math.floor(end.diff(start, 'minutes'))));
		}

		protected formatTime(totalMinutes: number, short?: boolean): string {
			let hours = Math.floor(totalMinutes / 60),
				minutes = totalMinutes - (hours * 60);
			return (isNaN(hours) ? '0' : hours + (short ? ':' : 'h '))
				+ (isNaN(minutes) ? '0' : (short ? (minutes < 10 ? '0' : '') + minutes : (minutes + 'm')));
		}

		public isClockedOut(): boolean {
			return this.event && this.event.time_event_tag && this.event.time_event_tag.tag_type === TagType.CLOCKED_OUT;
		}

		public loadEvents(): null|ng.IPromise<void> {
			this.loadingEvents = true;
			if (!this.userLoaded) {
				this.loadEventsPending = true;
				return null;
			}

			return this.TimeEventService
				.getTimeEvents({
					user: this.userId,
					show_length: 1,
					start_date: moment().format('YYYY-MM-DD'),
				})
				.then(r => this.events = r.data)
				.then(() => this.calculateTotalTime())
				.catch(e => this.ToastService.showError('Failed to get today\'s events: ', e))
				.finally(() => this.loadingEvents = false);
		}

		public getEventStartDate(event: TimeEvent): string {
			return moment(String(event.date), 'X').format('h:mm a');
		}
		public getEventEndDate(event: TimeEvent): string {
			if (event.length === null) {
				return '';
			}

			return moment(String(event.date + (event.length * 60)), 'X').format('h:mm a');
		}
		public getEventTotal(event: TimeEvent): string {
			if (event.length === null) {
				return '';
			}

			return this.formatTime(event.length, true);
		}
		public calculateTotalTime(): void {
			this.formattedTotal = this.formatTime(
				this.events
					.reduce((a, c) => a + (c.time_event_tag.paid && c.length ? c.length : 0), 0),
				true
			);
		}
	}

	export class TimeEventViewController extends TimeEventController {
		public static current_event: TimeEvent;
		public static instance: TimeEventViewController;
		public static getStatusColor(): string {
			return this.current_event && this.current_event.time_event_tag
				? this.current_event.time_event_tag.color
				: 'red';
		}
		public static getEventTagName(): string {
			return this.current_event
				&& this.current_event.time_event_tag
				&& this.current_event.time_event_tag.name
				|| null;
		}

		public tags: TimeEventTag[] = [];
		protected menu: App.User.UserMenuController;
		public appointment: App.Appointments.Appointment;

		static $inject = ['$q', 'TimeEventService', 'AppointmentService', 'SelectedAppointment', 'UserSession', 'ToastService'];
		constructor(
			protected $q: ng.IQService,
			TimeEventService: TimeEventService,
			AppointmentService: App.Appointments.AppointmentService,
			protected SelectedAppointment: App.Appointments.SelectedAppointment,
			UserSession: App.User.UserSession,
			ToastService: App.Utils.ToastService
		) {
			super(TimeEventService, UserSession, ToastService);
			TimeEventViewController.instance = this;

			AppointmentService.addStatusListener(() => {
				this.updateEvent();
			});

			SelectedAppointment.addListener(this.setAppointment.bind(this));

			$q.all([
				this.TimeEventService
					.getTags({ orderBy: 'name', sort: 'asc' })
					.then(r => this.tags = r.data)
					.catch(e => ToastService.showError('Failed to get event tags: ', e)),
				this.updateEvent()
					.catch(e => ToastService.showError('Failed to get event: ', e)),
				SelectedAppointment.getAppointment()
					.then(this.setAppointment.bind(this))
					.catch(angular.noop)
			]).then(() => this.loading = false);
		}

		public setAppointment(appt: App.Appointments.Appointment): void {
			this.appointment = appt;
		}

		public setMenu(menu: App.User.UserMenuController): void {
			this.menu = menu;
		}

		protected updateEvent(): ng.IPromise<TimeEvent> {
			return this.TimeEventService.getLatestEvent()
				.then(e => TimeEventViewController.current_event = this.event = e);
		}

		public update(e: TimeEvent): void {
			if (this.event && this.event.id === e.id) {
				this.event = e;
			}
			if (TimeEventViewController.current_event && TimeEventViewController.current_event.id === e.id) {
				TimeEventViewController.current_event = e;
			}
		}

		public formatInterval(): string {
			if (!this.event) {
				return '';
			}

			return super.formatInterval(
				moment(String(this.event.date), 'X'),
				moment()
			);
		}

		public hideTag(tag: TimeEventTag): boolean {
			return !tag
				|| tag.tag_type === TagType.CLOCKED_OUT
				|| ([TagType.TRAVELING, TagType.ON_SITE].indexOf(tag.tag_type) !== -1 && !this.SelectedAppointment.id)
		}

		public startClock(tag: TimeEventTag): void {
			if (this.saving || (this.event && this.event.time_event_tag.id === tag.id)) {
				return;
			}

			this.saving = true;
			this.TimeEventService
				.create({
					time_event_tag: tag.id,
					appointment: this.SelectedAppointment.id,
				})
				.then(r => TimeEventViewController.current_event = this.event = r.data)
				.then(() => this.TimeEventService.confirmLastEvent())
				.then(() => this.menu && (this.menu.showTimeEvents = false))
				.catch(e => this.ToastService.showError('Failed to change clock: ', e))
				.finally(() => this.saving = false);
		}

		public clockOut(): void {
			if (!this.event) {
				return;
			}
			const clockedOut = this.tags.filter(t => t.tag_type === TagType.CLOCKED_OUT);
			if (clockedOut.length < 1) {
				this.ToastService.show('Clocked out status not found', App.Utils.ToastService.ERROR_SELECTOR);
				return;
			}

			this.startClock(clockedOut[0]);
		}
	}

	export class TimeEventEditController extends TimeEventController {
		private static instance: TimeEventEditController;
		private static _$q: ng.IQService;

		public static confirmEvent(
			confirmEvent: TimeEvent,
			prevEvent: TimeEvent
		): ng.IPromise<{}> {
			return TimeEventEditController.instance.init(confirmEvent, prevEvent);
		}

		protected defer: ng.IDeferred<{}>;
		protected active: boolean = false;

		protected prevEvent: TimeEvent;

		protected appointment: App.Appointments.Appointment;

		protected date: Date;
		protected time: Date;
		protected limit: moment.Moment;

		static $inject = ['$q', 'SelectedAppointment', 'TimeEventService', 'UserSession', 'ToastService'];
		constructor(
			protected $q: ng.IQService,
			protected SelectedAppointment: App.Appointments.SelectedAppointment,
			TimeEventService: TimeEventService,
			UserSession: App.User.UserSession,
			ToastService: App.Utils.ToastService
		) {
			super(TimeEventService, UserSession, ToastService);
			TimeEventEditController.instance = this;
		}

		public resolve(): void {
			if (this.defer) {
				this.defer.resolve();
				this.defer = null;
				this.event = null;
			}
		}

		public formatInterval(): string {
			return super.formatInterval(
				this.limit,
				this.getMoment(this.date, this.time)
			);
		}

		public resetDate(): void {
			this.date = moment(moment(String(this.event.date), 'X').format('YYYY-MM-DD')).toDate();
			this.time = this.getDateFromHours(moment(String(this.event.date), 'X')).toDate();
		}

		protected getDateFromHours(hours: moment.Moment): moment.Moment {
			return moment('1970-01-01, ' + hours.format('HH:mm'), 'YYYY-MM-DD, HH:mm');
		}

		public getError(): string|null {
			const date = this.getMoment(this.date, this.time),
				now = moment();

			if (!date.isSame(now, 'minute') && date.isAfter(now)) {
				return 'max';
			}

			if (!date.isSame(this.limit, 'minute') && date.isBefore(this.limit)) {
				return 'min';
			}

			return null;
		}

		public save(): void {
			this.saving = true;

			let date = this.getMoment(this.date, this.time),
				oDate = moment(moment(String(this.event.date), 'X').format('YYYY-MM-DD, HH:mm'), 'YYYY-MM-DD, HH:mm');

			if (Math.abs(oDate.diff(date, 'minutes')) > 0) {
				this.TimeEventService
					.update(this.event.id, {
						custom_date: date.unix(),
					})
					.then(r => this.event = r.data)
					.then(e => TimeEventViewController.instance.update(e))
					.then(_ => this.resolve())
					.catch((err) => this.ToastService.showError('Unable to update event: ', err))
					.finally(() => {
						this.saving = false;
					});
			} else {
				this.resolve();
				this.saving = false;
			}
		}

		public init(
			confirmEvent: TimeEvent,
			prevEvent: TimeEvent,
		): ng.IPromise<any> {
			this.resolve();

			this.defer = this.$q.defer();

			this.event = confirmEvent;
			this.prevEvent = prevEvent;

			this.limit = moment(String(prevEvent.date), 'X');

			this.loading = true;
			this.saving = false;
			this.active = true;

			this.resetDate();

			this.SelectedAppointment.getAppointment()
				.then(appt => this.appointment = appt)
				.finally(() => this.loading = false);

			this.defer.promise.finally(() => this.active = false);

			return this.defer.promise;
		}
	}
}
